'use client';

import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface Base64ImagePreviewProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  alt?: string;
}

export default function Base64ImagePreview({ 
  isOpen, 
  onClose, 
  imageSrc,
  alt = "Image preview"
}: Base64ImagePreviewProps) {
  
  // Handle escape key to close
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleWrapperClick = (event: React.MouseEvent) => {
    const target = event.target as Element;
    
    // Don't close if clicking on the image itself
    if (target.tagName === 'IMG') {
      return;
    }
    
    // Close the preview popup
    onClose();
  };

  if (!isOpen || !imageSrc) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-50 p-4">
      <div 
        className="relative w-full h-full flex items-center justify-center"
        onClick={handleWrapperClick}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 text-white hover:text-gray-300 transition-colors duration-200 bg-black/60 backdrop-blur-sm rounded-full p-2 shadow-lg"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Image container */}
        <div className="relative max-w-full max-h-[90vh]">
          <img
            src={imageSrc}
            alt={alt}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    </div>
  );
}
